import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { formatPriceNumber, fromM2, toM2 } from "../function";
import * as s from "../InputPage.styles";
import { AnalysisForm, AreaUnit, areaUnitList } from "../machine";
import { useFormattedNumberField } from "./useFormattedNumberField";
import WeatherService from "../../../service/land/weather/weatherApi";
import styled from "styled-components";

interface Props {
  formData: AnalysisForm;
  setFormData: Dispatch<SetStateAction<AnalysisForm>>;
}

function WorkDaysSection({ formData, setFormData }: Props) {
  const [rainyDays, setRainyDays] = useState(0);
  const [region, setRegion] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const getRainyDays = async () => {
    setRainyDays(0);
    setIsLoading(true);
    setIsCustom(false);
    try {
      const res = await WeatherService.rainyDays(formData.pnu);
      setRainyDays(Number(res.rain_cnt));
      setRegion(res.region);
      setFormData((prev) => ({
        ...prev,
        rainyDays: Number(res.rain_cnt),
      }));
    } catch (error) {
      setIsError(true);
      setIsCustom(true);
    } finally {
      setIsLoading(false);
    }
  };

  const {
    display: growthDaysDisplay,
    handleChange: handleGrowthDaysChange,
    handleBlur: handleGrowthDaysBlur,
  } = useFormattedNumberField({
    baseValue: formData.growthDays,
    min: 1,
    max: 365,
    isPrice: false,
    onChangeBaseValue: (value) =>
      setFormData((prev) => ({ ...prev, growthDays: value })),
  });

  const {
    display: rainyDaysDisplay,
    handleChange: handleRainyDaysChange,
    handleBlur: handleRainyDaysBlur,
  } = useFormattedNumberField({
    baseValue: formData.rainyDays,
    isPrice: false,
    onChangeBaseValue: (value) =>
      setFormData((prev) => ({ ...prev, rainyDays: value })),
  });

  const {
    display: lossDaysDisplay,
    handleChange: handleLossDaysChange,
    handleBlur: handleLossDaysBlur,
  } = useFormattedNumberField({
    baseValue: formData.lossDays,
    isPrice: false,
    onChangeBaseValue: (value) =>
      setFormData((prev) => ({ ...prev, lossDays: value })),
  });

  const {
    display: monthDaysDisplay,
    handleChange: handleMonthDaysChange,
    handleBlur: handleMonthDaysBlur,
  } = useFormattedNumberField({
    baseValue: formData.monthDays,
    isPrice: false,
    onChangeBaseValue: (value) =>
      setFormData((prev) => ({ ...prev, monthDays: value })),
  });

  const {
    display: lossMachineDaysDisplay,
    handleChange: handleLossMachineDaysChange,
    handleBlur: handleLossMachineDaysBlur,
  } = useFormattedNumberField({
    baseValue: formData.lossMachineDays,
    isPrice: false,
    onChangeBaseValue: (value) =>
      setFormData((prev) => ({ ...prev, lossMachineDays: value })),
  });

  const {
    display: workingHoursDisplay,
    handleChange: handleWorkingHoursChange,
    handleBlur: handleWorkingHoursBlur,
  } = useFormattedNumberField({
    baseValue: formData.workingHours,
    isPrice: false,
    onChangeBaseValue: (value) =>
      setFormData((prev) => ({ ...prev, workingHours: value })),
  });

  // const monthlyWorkDays = Math.max(
  //   0,
  //   30 -
  //     ((formData.rainyDays || 0) +
  //       (formData.lossDays || 0) +
  //       (formData.lossMachineDays || 0))
  // );

  // const monthlyWorkHours = monthlyWorkDays * (formData.workingHours || 0);

  // // 기상 손실일
  // const lossWeather =
  //   formData.growthDays > 0
  //     ? Math.round((formData.rainyDays / (formData.growthDays / 30)) * 10) / 10
  //     : 0;
  // // 유효작업가능일
  // const effectiveWorkingDays = Math.max(
  //   30 - (lossWeather + formData.lossMachineDays + formData.lossDays),
  //   0
  // );
  // const efd = Math.round((effectiveWorkingDays / 30) * 100);

  // // 일평균 작업가능시간
  // const avgWorkingHours =
  //   formData.growthDays > 0
  //     ? Math.round(
  //         ((formData.workingHours *
  //           (formData.growthDays - formData.rainyDays)) /
  //           formData.growthDays) *
  //           10
  //       ) / 10
  //     : 0;

  const [lossWeather, setLossWeather] = useState(0);

  useEffect(() => {
    setLossWeather(() => {
      if (formData.growthDays <= 0) return 0;
    
      return (
        Math.round((formData.rainyDays / (formData.growthDays / 30)) * 10) / 10
      );
    })
  }, [formData.rainyDays, formData.growthDays]);

  const effectiveWorkingDays = useMemo(() => {
    const value =
      30 - (lossWeather + formData.lossMachineDays + formData.lossDays);
    return Math.max(value, 0);
  }, [lossWeather, formData.lossMachineDays, formData.lossDays]);

  useEffect(() => {
    const efd = Math.round((effectiveWorkingDays / 30) * 100);

    // 일평균 작업가능시간
    const avgWorkingHours =
      formData.growthDays > 0
        ? Math.round(
            ((formData.workingHours *
              (formData.growthDays - formData.rainyDays)) /
              formData.growthDays) *
              10
          ) / 10
        : 0;

    // formData에 넣기
    setFormData((prev) => ({
      ...prev,
      efd,
      avgWorkingHours,
    }));
  }, [
    lossWeather,
    formData.rainyDays,
    formData.lossMachineDays,
    formData.lossDays,
    formData.growthDays,
    formData.workingHours,
  ]);

  return (
    <s.Section>
      <s.SectionTitleContainer>
        <s.SectionTitle>3. 작업 가능일</s.SectionTitle>
      </s.SectionTitleContainer>

      <s.SectionInner $top={8}>
        <s.Label>
          <s.LabelTitle>생육일수</s.LabelTitle>
        </s.Label>
        <s.Stepper>
          <s.StepTextButton
            $bg="#F1F1FE"
            $color="#4B47FC"
            onClick={() =>
              setFormData((prev) => ({
                ...prev,
                growthDays: Math.max(prev.growthDays - 10, 1),
              }))
            }
          >
            -10일
          </s.StepTextButton>
          <s.StepperInner>
            <s.StepperInput
              value={growthDaysDisplay}
              onChange={(e) => handleGrowthDaysChange(e.target.value)}
              onBlur={handleGrowthDaysBlur}
            />

            <s.StepperUnit>일</s.StepperUnit>
          </s.StepperInner>
          <s.StepTextButton
            $bg="#FFF6F6"
            $color="#EC4F4F"
            onClick={() =>
              setFormData((prev) => ({
                ...prev,
                growthDays: Math.min(prev.growthDays + 10, 365),
              }))
            }
          >
            +10일
          </s.StepTextButton>
        </s.Stepper>
      </s.SectionInner>

      <s.SectionInner $top={8}>
        <s.Label>
          <s.LabelTitle>생육기간 우천일수</s.LabelTitle>
        </s.Label>
        <s.Button onClick={getRainyDays}>
          {isLoading ? <Spinner /> : "기상청 불러오기"}
        </s.Button>
        {!!rainyDays && (
          <s.InputMessageContainer>
            <s.InputWrapper>
              <s.Input
                value={rainyDaysDisplay}
                placeholder="우천일을 직접 입력해주세요"
                disabled={!isCustom}
                onChange={(e) => handleRainyDaysChange(e.target.value)}
                onBlur={handleRainyDaysBlur}
              />
              {isCustom ? (
                <span>일</span>
              ) : (
                <CloseButton
                  src="/asset/images/close/CloseGray20.svg"
                  alt=""
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      rainyDays: 0,
                    }));
                    setIsCustom(true);
                  }}
                />
              )}
            </s.InputWrapper>
            {!isCustom && (
              <s.InputMessage>{region} 생육기간 우천일</s.InputMessage>
            )}
            {isError && (
              <s.InputErrorMessage>
                데이터를 불러오지 못했습니다.
              </s.InputErrorMessage>
            )}
          </s.InputMessageContainer>
        )}
      </s.SectionInner>

      <s.SectionInner $top={8}>
        <RowInner>
          <Item>
            <s.Label>
              <s.LabelTitle>월 일수</s.LabelTitle>
            </s.Label>
            <s.InputWrapper>
              <s.Input
                value={monthDaysDisplay}
                placeholder="월 평균 기계 정비일 수를 입력해주세요"
                onChange={(e) => handleMonthDaysChange(e.target.value)}
                onBlur={handleMonthDaysBlur}
              />
              <span>일</span>
            </s.InputWrapper>
          </Item>
          <Item>
            <s.Label>
              <s.LabelTitle>월 기상 손실일</s.LabelTitle>
            </s.Label>
            <s.InputWrapper>
            <s.Input
              value={lossWeather}
              placeholder="월 기상 손실일 수를 입력해주세요"
              onChange={(e) => setLossWeather(Number(e.target.value))}
              // onBlur={handleMonthDaysBlur}
            />
              <span>일</span>
            </s.InputWrapper>
            {/* <s.FixedValue>{lossWeather}</s.FixedValue> */}
          </Item>
        </RowInner>
      </s.SectionInner>

      <s.SectionInner $top={8}>
        <s.Label>
          <s.LabelTitle>월 기계 정비 손실일</s.LabelTitle>
        </s.Label>
        <s.InputWrapper>
          <s.Input
            value={lossMachineDaysDisplay}
            placeholder="월 평균 기계 정비일 수를 입력해주세요"
            onChange={(e) => handleLossMachineDaysChange(e.target.value)}
            onBlur={handleLossMachineDaysBlur}
          />
          <span>일</span>
        </s.InputWrapper>
      </s.SectionInner>

      <s.SectionInner $top={8}>
        <s.Label>
          <s.LabelTitle>월 휴일</s.LabelTitle>
        </s.Label>
        <s.InputWrapper>
          <s.Input
            value={lossDaysDisplay}
            placeholder="월 평균 휴일 수를 입력해주세요"
            onChange={(e) => handleLossDaysChange(e.target.value)}
            onBlur={handleLossDaysBlur}
          />
          <span>일</span>
        </s.InputWrapper>
      </s.SectionInner>

      <s.SectionInner>
        <s.Label>
          <s.LabelTitle>월 작업가능일, 유효비율</s.LabelTitle>월 기준 손실일을
          반영해 실제 일할 수 있는 날과 비율입니다.
        </s.Label>
        <s.FixedValue>
          {effectiveWorkingDays}일, {formData.efd / 100} ({formData.efd}%)
        </s.FixedValue>
      </s.SectionInner>

      <s.SectionInner $top={8}>
        <s.Label>
          <s.LabelTitle>하루 평균 작업시간</s.LabelTitle>
        </s.Label>
        <s.InputWrapper>
          <s.Input
            value={workingHoursDisplay}
            placeholder="일 평균 작업 시간을 입력해주세요"
            onChange={(e) => handleWorkingHoursChange(e.target.value)}
            onBlur={handleWorkingHoursBlur}
          />
          <span>시간</span>
        </s.InputWrapper>
      </s.SectionInner>
      <s.SectionInner>
        <s.Label>
          <s.LabelTitle>일 평균 환산 작업가능시간</s.LabelTitle>
          우천일을 반영해 작업가능일 기준으로 환산한 평균 작업시간입니다.
        </s.Label>
        <s.FixedValue>{formData.avgWorkingHours}시간</s.FixedValue>
      </s.SectionInner>
      {/* <s.SectionInner>
        <s.Label>
          <s.LabelTitle>1달 작업 일수, 시간</s.LabelTitle>
          30일 기준 작업 일수와 시간이 계산됩니다.
        </s.Label>
        <s.FixedValue>
          {monthlyWorkDays}일, {monthlyWorkHours}시간
        </s.FixedValue>
      </s.SectionInner> */}
    </s.Section>
  );
}

export default WorkDaysSection;

const CloseButton = styled.img`
  cursor: pointer;
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 3px solid #ffffff55;
  border-top: 3px solid #1e9b6b; /* 초록색 */
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const RowInner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  min-width: 0;
`;
